// custom-floating-checkout-cart.js
// Floating mobile checkout bar — mirrors PDP sticky ATC behavior (show when primary checkout is off-screen)

(() => {
  const MOBILE_MAX_WIDTH = 749;
  const PRIMARY_CHECKOUT_SELECTOR = 'main button#checkout.cart__checkout-button';
  const STYLES_ID = 'floating-checkout-styles';
  /* Shared with the PDP sticky ATC — fixed siblings (promo pill, chat launcher) offset from it */
  const CLEARANCE_PROPERTY = '--custom-floating-atc-clearance';

  /* Loaded only on cart template via scripts.liquid; cart layout marker */
  if (!document.querySelector('cart-items-component')) return;

  /** @type {(() => void) | null} */
  let teardown = null;

  function injectStyles() {
    if (document.getElementById(STYLES_ID)) return;

    const style = document.createElement('style');
    style.id = STYLES_ID;
    style.textContent = `
      .floating-cart-checkout {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 40;
        padding: 0.75rem 1rem calc(env(safe-area-inset-bottom) + 0.75rem);
        background: var(--color-background, #fff);
        box-shadow: 0 -2px 12px rgba(0,0,0,0.12);
        display: flex;
        justify-content: center;
      }
      .floating-cart-checkout[hidden] { display: none; }
      .floating-cart-checkout__button {
        width: 100%;
        max-width: 480px;
        height: clamp(25px, var(--height-buy-buttons), 55px);
      }
      /* !important: Gorgias writes an inline bottom on its own launcher */
      #chat-button { transition: bottom 0.3s ease !important; }
      body.floating-atc-visible #chat-button {
        bottom: calc(var(${CLEARANCE_PROPERTY}, 80px) + 10px) !important;
      }

      @media (min-width: 750px) {
        .floating-cart-checkout { display: none !important; }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * @param {HTMLButtonElement} floatBtn
   * @param {HTMLButtonElement} primary
   */
  function syncButton(floatBtn, primary) {
    floatBtn.disabled = primary.disabled;
    floatBtn.className = `${primary.className} floating-cart-checkout__button`.trim();

    const label = primary.textContent?.trim() || '';
    floatBtn.textContent = label;
    floatBtn.setAttribute('aria-label', primary.getAttribute('aria-label') || label);
  }

  /**
   * @param {HTMLElement} container
   */
  function syncColorScheme(container) {
    const schemeSource = document.querySelector('.cart__summary-inner');
    if (!schemeSource) return;

    Array.from(container.classList)
      .filter((c) => c.startsWith('color-scheme-'))
      .forEach((c) => container.classList.remove(c));
    Array.from(schemeSource.classList)
      .filter((c) => c.startsWith('color-scheme-'))
      .forEach((c) => container.classList.add(c));
  }

  /**
   * Single place the shown/hidden state is written, so the clearance property never outlives the bar.
   * @param {HTMLElement} container
   * @param {boolean} shouldShow
   */
  function setVisible(container, shouldShow) {
    container.toggleAttribute('hidden', !shouldShow);
    document.body.classList.toggle('floating-atc-visible', shouldShow);
    if (!shouldShow) document.body.style.removeProperty(CLEARANCE_PROPERTY);
  }

  /**
   * @param {HTMLButtonElement} primary
   */
  function isPrimaryButtonVisible(primary) {
    const rect = primary.getBoundingClientRect();
    return rect.top >= 0 && rect.bottom <= window.innerHeight;
  }

  /**
   * @param {HTMLElement} container
   * @param {HTMLButtonElement} primaryCheckout
   */
  function updateVisibility(container, primaryCheckout) {
    const isShown = !container.hasAttribute('hidden');
    const shouldShow = window.innerWidth <= MOBILE_MAX_WIDTH && !isPrimaryButtonVisible(primaryCheckout);

    if (shouldShow !== isShown) setVisible(container, shouldShow);
  }

  function init() {
    teardown?.();
    teardown = null;

    document.querySelector('.floating-cart-checkout')?.remove();
    document.body.classList.remove('floating-atc-visible');
    document.body.style.removeProperty(CLEARANCE_PROPERTY);

    if (window.innerWidth > MOBILE_MAX_WIDTH) return;

    const primaryCheckoutEl = document.querySelector(PRIMARY_CHECKOUT_SELECTOR);
    if (!(primaryCheckoutEl instanceof HTMLButtonElement)) return;

    injectStyles();

    const container = document.createElement('div');
    container.className = 'floating-cart-checkout';
    container.setAttribute('hidden', '');

    const btn = document.createElement('button');
    btn.type = 'button';

    btn.addEventListener('click', () => {
      if (!primaryCheckoutEl.disabled) primaryCheckoutEl.click();
    });

    container.appendChild(btn);
    document.body.appendChild(container);

    syncButton(btn, primaryCheckoutEl);
    syncColorScheme(container);

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        if (entry.isIntersecting && entry.intersectionRatio > 0) {
          setVisible(container, false);
        } else if (window.innerWidth <= MOBILE_MAX_WIDTH) {
          setVisible(container, true);
        }
      },
      { threshold: [0] }
    );

    /* Publishes the bar's real height (button size, safe-area inset) instead of a hardcoded offset */
    const resizeObserver = new ResizeObserver((entries) => {
      const height = entries[entries.length - 1]?.borderBoxSize?.[0]?.blockSize ?? 0;
      if (height > 0 && !container.hasAttribute('hidden')) {
        document.body.style.setProperty(CLEARANCE_PROPERTY, `${Math.round(height)}px`);
      }
    });
    resizeObserver.observe(container);

    const mutationObserver = new MutationObserver(() => {
      syncButton(btn, primaryCheckoutEl);
      syncColorScheme(container);
    });

    intersectionObserver.observe(primaryCheckoutEl);
    mutationObserver.observe(primaryCheckoutEl, { attributes: true, childList: true, subtree: true });

    const onLifecycle = () => requestAnimationFrame(init);
    document.addEventListener('cart:update', onLifecycle);
    document.addEventListener('shopify:section:load', onLifecycle);

    teardown = () => {
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      document.removeEventListener('cart:update', onLifecycle);
      document.removeEventListener('shopify:section:load', onLifecycle);
      container.remove();
      document.body.classList.remove('floating-atc-visible');
      document.body.style.removeProperty(CLEARANCE_PROPERTY);
    };

    updateVisibility(container, primaryCheckoutEl);
  }

  /** Resize covers mobile ↔ desktop and matches PDP resize + intersection behavior without duplicate listeners */
  window.addEventListener('resize', init);
  init();
})();
